import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import ChangePasswordActions from '../../../redux/ChangePasswordRedux';
import './ChangePasswordPage.css';

interface ChangePasswordPageProps {
  // Redux props
  changePasswordFetching?: boolean;
  changePasswordSuccess?: boolean;
  changePasswordError?: boolean;
  changePasswordErrorMessage?: any;
  changePassword?: (password: string, oldPassword: string) => void;
  
  // Translation props
  t?: (key: string) => string;
  
  // Navigation props (commented for Phase 2)
  navigate?: (path: string) => void;
}

interface ChangePasswordPageState {
  currentPassword: string | null;
  newPassword: string | null;
  confirmPassword: string | null;
  props?: any; // For getDerivedStateFromProps
}

class ChangePasswordPage extends Component<ChangePasswordPageProps, ChangePasswordPageState> {
  constructor(props: ChangePasswordPageProps) {
    super(props);
    this.state = {
      currentPassword: null,
      newPassword: null,
      confirmPassword: null,
      props: props,
    };
  }

  componentDidMount() {
    // Component mounted
  }

  componentDidUpdate(prevProps: ChangePasswordPageProps) {
    // Handle success and error alerts safely in componentDidUpdate
    // Check if request just completed (was fetching, now not fetching)
    if (prevProps.changePasswordFetching && !this.props.changePasswordFetching) {
      // Change password request completed
      
      // Success handling
      if (this.props.changePasswordSuccess) {
        const successMessage = this.props.t ? this.props.t('change-password-screen:alert.success') : 'Update password successful';
        alert(successMessage);
        
        // Clear form after success
        this.setState({
          currentPassword: null,
          newPassword: null,
          confirmPassword: null,
        });
        
        // Navigate back to settings page after success (with small delay for better UX)
        if (this.props.navigate) {
          setTimeout(() => {
            this.props.navigate!('/settings');
          }, 1000); // 1 second delay to allow user to read success message
        }
      }
      
      // Error handling
      if (this.props.changePasswordError) {
        // Use API error message if available, otherwise use fallback
        let errorMessage = 'Your current password is missing or incorrect.';
        
        if (this.props.changePasswordErrorMessage) {
          // Check if error message is an object with message property (common API error format)
          if (typeof this.props.changePasswordErrorMessage === 'object' && this.props.changePasswordErrorMessage?.message) {
            errorMessage = this.props.changePasswordErrorMessage.message;
          } 
          // Check if it's a string directly
          else if (typeof this.props.changePasswordErrorMessage === 'string') {
            errorMessage = this.props.changePasswordErrorMessage;
          }
          // Handle array of errors (some APIs return arrays)
          else if (Array.isArray(this.props.changePasswordErrorMessage) && this.props.changePasswordErrorMessage.length > 0) {
            errorMessage = this.props.changePasswordErrorMessage[0];
          }
          
          // Using API error message
        }
        
        // Use translation for fallback if no API message
        if (errorMessage === 'Your current password is missing or incorrect.' && this.props.t) {
          errorMessage = this.props.t('change-password-screen:alert.error');
        }
        
        alert(errorMessage);
      }
    }
  }

  // Phase 2: Navigation methods
  back = () => {
    if (this.props.navigate) {
      this.props.navigate('/settings');
    }
  }

  // Phase 4 & 6: Form submission method with enhanced validation
  _onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission
    
    // Phase 6: Enhanced validation with password length check
    if (!this.state.currentPassword) {
      const errorMessage = this.props.t ? this.props.t('change-password-screen:alert.current-password-error') : 'Your current password is missing or incorrect.';
      alert(errorMessage);
    } else if (!this.state.newPassword || !this.state.confirmPassword) {
      const errorMessage = this.props.t ? this.props.t('change-password-screen:alert.fields-required') : 'All fields are required.';
      alert(errorMessage);
    } else if (this.state.newPassword.length < 6) {
      const errorMessage = this.props.t ? this.props.t('change-password-screen:alert.password-length-error') : 'Password must contain at least 6 characters.';
      alert(errorMessage);
    } else if (this.state.newPassword !== this.state.confirmPassword) {
      const errorMessage = this.props.t ? this.props.t('change-password-screen:alert.new-password-and-confirm-password-error') : 'Your new password and confirm password do not match';
      alert(errorMessage);
    } else if (this.props.changePassword) {
      this.props.changePassword(this.state.newPassword, this.state.currentPassword);
    }
  }

  // Phase 6: Redux state management - only pure state updates, no side effects
  static getDerivedStateFromProps(nextProps: ChangePasswordPageProps, prevState: ChangePasswordPageState) {
    // Only track props changes, no alerts or side effects here
    return {
      ...prevState,
      props: nextProps
    };
  }

  render() {
    return (
      <div className="change-password-page">
        {/* Header with back button and title */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <button 
                className="header-button back-button" 
                onClick={this.back}
              >
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
              </button>
              <h1 className="header-title">
                  {this.props.t ? this.props.t('change-password-screen:title') : 'Change Password'}
                </h1>
            </div>
          </div>
        </div>

        {/* Main content with form */}
        <div className="change-password-content">
          <p className="change-password-message">
            {this.props.t ? this.props.t('change-password-screen:message') : 'Please enter your current password and the new password. Password must contain at least 6 characters.'}
          </p>

          {/* HTML Form for better UX and accessibility */}
          <form onSubmit={this._onFormSubmit} noValidate>
            {/* Current Password Field */}
            <div className="form-group">
              <label className="form-label" htmlFor="current-password">
                {this.props.t ? this.props.t('change-password-screen:input.label.current-password') : 'CURRENT PASSWORD:'}
              </label>
              <input
                id="current-password"
                name="currentPassword"
                type="password"
                className="form-input"
                placeholder={this.props.t ? this.props.t('change-password-screen:input.placeholder.current-password') : 'Current password'}
                value={this.state.currentPassword || ''}
                onChange={(e) => this.setState({ currentPassword: e.target.value })}
                autoCapitalize="none"
                autoComplete="current-password"
                required
              />
            </div>

            {/* New Password Field */}
            <div className="form-group">
              <label className="form-label" htmlFor="new-password">
                {this.props.t ? this.props.t('change-password-screen:input.label.new-password') : 'NEW PASSWORD:'}
              </label>
              <input
                id="new-password"
                name="newPassword"
                type="password"
                className="form-input"
                placeholder={this.props.t ? this.props.t('change-password-screen:input.placeholder.new-password') : 'New password'}
                value={this.state.newPassword || ''}
                onChange={(e) => this.setState({ newPassword: e.target.value })}
                autoCapitalize="none"
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>

            {/* Confirm Password Field */}
            <div className="form-group">
              <label className="form-label" htmlFor="confirm-password">
                {this.props.t ? this.props.t('change-password-screen:input.label.confirm-password') : 'CONFIRM PASSWORD:'}
              </label>
              <input
                id="confirm-password"
                name="confirmPassword"
                type="password"
                className="form-input"
                placeholder={this.props.t ? this.props.t('change-password-screen:input.placeholder.confirm-password') : 'Confirm password'}
                value={this.state.confirmPassword || ''}
                onChange={(e) => this.setState({ confirmPassword: e.target.value })}
                autoCapitalize="none"
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="submit-button-container">
              <button
                type="submit"
                className="submit-button comman-btn w-100"
                disabled={this.props.changePasswordFetching}
              >
                {this.props.changePasswordFetching 
                  ? (this.props.t ? this.props.t('common:loading') : 'Loading...') 
                  : (this.props.t ? this.props.t('change-password-screen:button.submit') : 'SUBMIT')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

// Phase 5: Redux connection
const mapStateToProps = (state: any) => {
  return {
    changePasswordFetching: state.changePassword.fetching,
    changePasswordSuccess: state.changePassword.success,
    changePasswordError: state.changePassword.error,
    changePasswordErrorMessage: state.changePassword.errorMessage
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    changePassword: (password: string, oldPassword: string) => 
      dispatch(ChangePasswordActions.changePasswordRequest(password, oldPassword))
  };
};

// Phase 5: Redux & Translation HOC wrapping
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ChangePasswordPage));
